/**
 * Proactive JWT refresh scheduler.
 *
 * Renews the access token BEFORE it expires so that no request ever carries a dead token. Without
 * this, a backgrounded tab whose token has lapsed wakes up and fires dozens of requests all bearing
 * the expired token; the interceptor recovers by refreshing once and replaying, but the backend
 * still eats N failed auth attempts plus the downstream SQL for each before the replay succeeds.
 *
 * This is an optimisation, not a safety net. When the proactive refresh fails the app degrades to
 * exactly the pre-existing reactive 401-then-refresh behaviour — which is why the failure path here
 * deliberately does nothing.
 *
 * Imports from `./client` only (one direction). `client.ts` must NOT import this file.
 */

import {
  getAccessToken,
  isImpersonating,
  onAccessTokenChange,
  refreshAccessToken,
} from "./client";

// ── Constants ─────────────────────────────────────────────────────────────────

/**
 * Fraction of the token's total lifetime at which we schedule the refresh.
 * 0.8 on a 20-minute token = refresh at 16 minutes, leaving a 4-minute buffer.
 */
const REFRESH_AT_FRACTION_OF_LIFETIME = 0.8;

/**
 * Minimum remaining lifetime (ms) below which we refresh immediately rather than scheduling a
 * timer. A token with under a minute of life is not worth waiting on — by the time the timer fires,
 * network jitter or browser throttling may have pushed it past expiry.
 */
const IMMEDIATE_REFRESH_THRESHOLD_MS = 60_000;

// ── JWT helpers (local to this file) ──────────────────────────────────────────
// `client.ts`'s `isImpersonating()` and `use-auth.tsx`'s `decodeJwt` both decode JWTs already;
// neither is exported in a shape this file can reuse, and the spec says not to re-wire them.
// A minimal decode here avoids coupling and keeps the function contract (never throws) simple.

/**
 * Decodes a JWT's payload segment. Returns `null` for anything that is not a well-formed token.
 * This is untrusted input from localStorage — a malformed token must return `null`, never throw.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;

    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));

    return typeof payload === "object" && payload !== null ? payload : null;
  } catch {
    return null;
  }
}

/** Reads a numeric seconds-since-epoch claim and returns it as epoch milliseconds. */
function readEpochClaimMs(token: string, claim: "exp" | "iat"): number | null {
  const value = decodeJwtPayload(token)?.[claim];
  return typeof value === "number" ? value * 1000 : null;
}

/**
 * Extracts the `exp` claim from a JWT as epoch milliseconds.
 * Returns `null` for anything that is not a well-formed token with a numeric `exp`.
 */
export function getAccessTokenExpiry(token: string): number | null {
  return readEpochClaimMs(token, "exp");
}

// ── Token staleness ───────────────────────────────────────────────────────────

/**
 * The backend's `TokenValidationParameters.ClockSkew` is 30 s, so a token inside this margin is
 * still accepted by the server — the margin buys a full request round-trip of headroom rather than
 * racing the exact expiry boundary.
 */
const TOKEN_STALE_MARGIN_MS = 30_000;

/**
 * Whether a token is expired or within {@link TOKEN_STALE_MARGIN_MS} of expiry.
 *
 * Returns `false` for an unparseable token — fail open, let the server decide. Takes `nowMs` as a
 * parameter (rather than calling `Date.now()` internally) to match `computeRefreshDelayMs`'s
 * existing convention and keep the function pure/testable.
 */
export function isAccessTokenStale(token: string, nowMs: number): boolean {
  const expiryMs = getAccessTokenExpiry(token);
  if (expiryMs === null) return false;
  return expiryMs - nowMs <= TOKEN_STALE_MARGIN_MS;
}

// ── Scheduling logic ──────────────────────────────────────────────────────────

/**
 * Computes the number of milliseconds to wait before refreshing the token, or `null` meaning
 * "do not schedule anything" (no token, or token is unparseable).
 *
 * Rules, evaluated in order:
 *
 * 1. No token or no readable `exp` → `null`.
 * 2. Already expired or under IMMEDIATE_REFRESH_THRESHOLD_MS of life left → `0` (refresh now).
 * 3. If the token carries a numeric `iat`, schedule at `iat + 0.8 * (exp − iat)`.
 *    This is the correct reading of "80% of lifetime".
 * 4. Otherwise schedule at `now + 0.8 * (exp − now)` — 80% of the REMAINING time.
 *
 * Both branches produce 16 minutes on a freshly-issued 20-minute token, which is the target.
 *
 * NOTE: The backend builds tokens in LMSAPI.BLL/Services/AuthServices/AuthServices.cs via
 * `new JwtSecurityToken(...)` WITHOUT passing `notBefore` or `issuedAt`, so `iat` is almost
 * certainly absent in practice and rule 4 is the live path. Rule 3 exists because it is the
 * correct reading of "80% of lifetime" and costs three lines if the backend ever starts
 * emitting `iat` — it is NOT dead code by accident.
 */
export function computeRefreshDelayMs(
  token: string | null,
  nowMs: number,
): number | null {
  if (!token) return null;

  const expMs = getAccessTokenExpiry(token);
  if (expMs === null) return null;

  const remaining = expMs - nowMs;
  if (remaining <= IMMEDIATE_REFRESH_THRESHOLD_MS) return 0;

  const iatMs = readEpochClaimMs(token, "iat");
  if (iatMs !== null) {
    // Rule 3: 80% of the full lifetime measured from issued-at.
    const refreshAt = iatMs + REFRESH_AT_FRACTION_OF_LIFETIME * (expMs - iatMs);
    return Math.max(0, refreshAt - nowMs);
  }

  // Rule 4: 80% of the remaining time (no iat available).
  return REFRESH_AT_FRACTION_OF_LIFETIME * remaining;
}

// ── Scheduler ─────────────────────────────────────────────────────────────────

/**
 * Starts the proactive refresh scheduler. Returns a stop function that tears down every listener
 * and timer it owns.
 *
 * Safe to call on the server (`typeof window === "undefined"`) — returns a no-op stop function.
 *
 * Each call creates an INDEPENDENT scheduler with its own timer, rather than being idempotent the
 * way `installAuthInterceptor` is. The intended usage is exactly one call, inside the AuthProvider
 * effect, whose cleanup runs between React Strict Mode's double-invoke. Two concurrent schedulers
 * would still be harmless: `refreshAccessToken` is single-flight, so both timers firing produces
 * one network call, not two. Each call's stop function tears down only its own scheduler.
 */
export function startProactiveRefresh(): () => void {
  // SSR guard: timers and DOM events do not exist on the server.
  if (typeof window === "undefined") return () => {};

  let timerId: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  // ── arm / disarm ────────────────────────────────────────────────────────

  function clearPendingTimer(): void {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  function arm(): void {
    clearPendingTimer();
    if (stopped) return;

    const delayMs = computeRefreshDelayMs(getAccessToken(), Date.now());
    if (delayMs === null) return;

    timerId = setTimeout(() => {
      timerId = null;
      void fireRefresh();
    }, delayMs);
  }

  // ── refresh callback ───────────────────────────────────────────────────

  async function fireRefresh(): Promise<void> {
    // ⚠️ SAFETY: Do NOT refresh during an impersonation session.
    //
    // The refresh cookie in the browser belongs to the SuperAdmin, not the impersonated user.
    // Calling /Auth/refresh here would exchange the "viewing as student" token for a real
    // SuperAdmin session behind the user's back. The backend also rejects this (409), but this
    // check avoids a request we know will fail — and more importantly, prevents the scheduler
    // from re-arming on the resulting error and retrying in a tight loop.
    if (isImpersonating()) return;

    // `refreshAccessToken` reports failure by RETURNING false, not by throwing — it owns an
    // internal try/catch. Both outcomes are read here so the no-op is visibly deliberate rather
    // than something a reader has to infer from an empty catch.
    const refreshed = await refreshAccessToken().catch(() => false);

    if (refreshed) {
      // `refreshAccessToken` called `setAccessToken`, which fired the token-change listener below,
      // which called `arm()`. Do NOT also arm here — that would double-schedule the next refresh.
      return;
    }

    // Failure is deliberately inert: no removeAccessToken(), no `auth:unauthorized`, no retry, no
    // re-arm. The access token may still have minutes of life left, and the reactive
    // 401-then-refresh path in `install-auth-interceptor.ts` and `client.ts` is the proven backstop
    // (4,685 successful rotations in production). A proactive refresh is an optimisation; when it
    // fails the app must degrade to exactly the pre-existing behaviour. A future reader will be
    // tempted to "handle" this — resist the urge.
  }

  // ── token-change listener ──────────────────────────────────────────────
  // Re-arm on every token write (new token → new deadline) or removal (null → clear timer).

  const unsubscribeTokenChange = onAccessTokenChange(() => {
    arm();
  });

  // ── visibilitychange listener ──────────────────────────────────────────
  // Browsers throttle setTimeout to ≥1 minute in background tabs and freeze it entirely after a
  // few minutes. This is precisely the backgrounded-tab scenario this entire task exists to fix:
  // the user leaves, the timer never fires, the token expires, and when the user returns 152
  // requests fan out with a dead token. Re-arming on visibility change recomputes the delay from
  // the current time, so if the token lapsed while backgrounded the delay is 0 and the refresh
  // fires immediately on return — before any API call carries the dead token.

  function handleVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      arm();
    }
  }
  document.addEventListener("visibilitychange", handleVisibilityChange);

  // ── pagehide listener ──────────────────────────────────────────────────
  // A page entering the back/forward cache (bfcache) should not carry a live timer that fires on
  // restore against a stale deadline. The visibilitychange re-arm recomputes correctly when the
  // page comes back; this just clears the timer so it cannot fire during the freeze/restore gap.

  function handlePageHide(): void {
    clearPendingTimer();
  }
  window.addEventListener("pagehide", handlePageHide);

  // ── initial arm ────────────────────────────────────────────────────────
  arm();

  // ── stop function ──────────────────────────────────────────────────────

  return () => {
    if (stopped) return;
    stopped = true;
    clearPendingTimer();
    unsubscribeTokenChange();
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pagehide", handlePageHide);
  };
}
